import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Dialog } from "@mui/material";
import * as React from "react";

import {
  RobotoIconButton,
  RobotoTableCell,
  RobotoTableRow,
  RobotoTooltip,
} from "@/shared/components";
import { TriggerNameLink } from "@/shared/components/links";
import { TriggerEvaluationsForTrigger } from "@/shared/components/TriggerEvaluations";
import { UserLink } from "@/shared/components/UserLink";
import { TriggerView } from "@/shared/domain/actions";
import { useDeleteTrigger } from "@/shared/domain/actions/hooks";
import { OrgRecord } from "@/shared/domain/orgs";
import { ErrorMonitoringService } from "@/shared/services";

import { ActionLink } from "../action/ActionLink";

import { TriggerSwitch } from "./TriggerSwitch";

interface TriggerRowProps {
  trigger: TriggerView;
  currentOrg: OrgRecord | null;
  reloadToggle: boolean;
  setReloadToggle: (arg: boolean) => void;
  setAlertDialogText: (arg: string) => void;
  setAlertDialogAction: (
    value: React.SetStateAction<(() => Promise<void>) | undefined>,
  ) => void;
  setAlertDialogOpen: (arg: boolean) => void;
  setErrorText: (arg: string) => void;
}

export const TriggerRow: React.FC<TriggerRowProps> = ({
  trigger,
  currentOrg,
  reloadToggle,
  setReloadToggle,
  setAlertDialogText,
  setAlertDialogAction,
  setAlertDialogOpen,
  setErrorText,
}) => {
  const [openEvaluationHistory, setOpenEvaluationHistory] =
    React.useState(false);

  const deleteTriggerMutation = useDeleteTrigger();

  function wrapActionWithDialog(text: string, action: () => Promise<void>) {
    setAlertDialogText(text);
    setAlertDialogAction(() => action);
    setAlertDialogOpen(true);
  }
  return (
    <React.Fragment>
      <RobotoTableRow>
        <RobotoTableCell
          sx={{
            minWidth: "200px",
          }}
        >
          <TriggerNameLink triggerName={trigger.name} />
        </RobotoTableCell>
        <RobotoTableCell
          sx={{
            minWidth: "200px",
          }}
        >
          <ActionLink action={trigger.action} />
        </RobotoTableCell>
        <RobotoTableCell>
          <>
            <RobotoIconButton
              eventName={"TriggerEvaluationHistoryClicked"}
              eventProperties={{
                triggerId: trigger.trigger_id,
              }}
              onClick={() => setOpenEvaluationHistory(true)}
              size="small"
              title="View trigger evaluations history"
            >
              <OpenInNewIcon fontSize="small" color="primary" />
            </RobotoIconButton>
            <Dialog
              maxWidth={false}
              open={openEvaluationHistory}
              onClose={() => setOpenEvaluationHistory(false)}
            >
              <TriggerEvaluationsForTrigger
                triggerName={trigger.name}
                triggerType={trigger.trigger_type}
              />
            </Dialog>
          </>
        </RobotoTableCell>
        <RobotoTableCell title={new Date(trigger.modified).toLocaleString()}>
          {new Date(trigger.modified).toLocaleString()}
        </RobotoTableCell>
        <RobotoTableCell title={trigger.modified_by || undefined}>
          {trigger.modified_by.includes("@") ? (
            <UserLink userId={trigger.modified_by} />
          ) : (
            trigger.modified_by
          )}
        </RobotoTableCell>
        <RobotoTableCell
          sx={{
            minWidth: "200px",
          }}
        >
          <TriggerSwitch
            active={trigger.enabled}
            triggerId={trigger.trigger_id}
            triggerName={trigger.name}
            triggerType={trigger.trigger_type}
            setErrorText={setErrorText}
          />

          <RobotoTooltip title="Delete Trigger">
            <span>
              <RobotoIconButton
                eventName={"DeleteTriggerClicked"}
                eventProperties={{
                  triggerId: trigger.trigger_id,
                }}
                onClick={() =>
                  wrapActionWithDialog(
                    `Are you sure you want to delete ${trigger.name}?`,
                    async function () {
                      setErrorText("");
                      try {
                        await deleteTriggerMutation.mutateAsync({
                          name: trigger.name,
                          resourceOwnerId: currentOrg?.org_id ?? "",
                          triggerId: trigger.trigger_id,
                          triggerType: trigger.trigger_type,
                        });
                        setReloadToggle(!reloadToggle);
                      } catch (error) {
                        const err =
                          error instanceof Error
                            ? error
                            : new Error(String(error));
                        ErrorMonitoringService.captureError(err, { trigger });
                        setErrorText(err.message);
                      }
                    },
                  )
                }
                disabled={deleteTriggerMutation.isPending}
              >
                <DeleteIcon color="secondary" />
              </RobotoIconButton>
            </span>
          </RobotoTooltip>
        </RobotoTableCell>
      </RobotoTableRow>
    </React.Fragment>
  );
};
